import React, { useState, useCallback, useRef, useEffect } from "react";

// Utility functions for color conversion
const hexToRgb = hex => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      }
    : null;
};

const rgbToHex = (r, g, b) => {
  return (
    "#" +
    [r, g, b]
      .map(x => {
        const hex = x.toString(16);
        return hex.length === 1 ? "0" + hex : hex;
      })
      .join("")
  );
};

const hsvToRgb = (h, s, v) => {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let r, g, b;

  if (h >= 0 && h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (h >= 60 && h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (h >= 120 && h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (h >= 180 && h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (h >= 240 && h < 300) {
    r = x;
    g = 0;
    b = c;
  } else {
    r = c;
    g = 0;
    b = x;
  }

  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255)
  };
};

const rgbToHsv = (r, g, b) => {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const diff = max - min;

  let h = 0;
  if (diff !== 0) {
    if (max === r) {
      h = ((g - b) / diff) % 6;
    } else if (max === g) {
      h = (b - r) / diff + 2;
    } else {
      h = (r - g) / diff + 4;
    }
  }
  h = Math.round(h * 60);
  if (h < 0) h += 360;

  const s = max === 0 ? 0 : diff / max;
  const v = max;

  return { h, s, v };
};

const isValidHex = hex => {
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex);
};

// Custom Color Picker Component
const CustomColorPicker = ({ color, onChange }) => {
  const canvasRef = useRef(null);
  const sliderRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isSliderDragging, setIsSliderDragging] = useState(false);
  const [hue, setHue] = useState(0);
  const [saturation, setSaturation] = useState(1);
  const [value, setValue] = useState(1);

  // Initialize HSV from hex color
  useEffect(() => {
    if (color) {
      const rgb = hexToRgb(color);
      if (rgb) {
        const hsv = rgbToHsv(rgb.r, rgb.g, rgb.b);
        setHue(hsv.h);
        setSaturation(hsv.s);
        setValue(hsv.v);
      }
    }
  }, [color]);

  // Draw the saturation/value canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const width = canvas.width;
    const height = canvas.height;

    // Create gradient
    const imageData = ctx.createImageData(width, height);
    const data = imageData.data;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const s = x / width;
        const v = 1 - y / height;
        const rgb = hsvToRgb(hue, s, v);

        const index = (y * width + x) * 4;
        data[index] = rgb.r;
        data[index + 1] = rgb.g;
        data[index + 2] = rgb.b;
        data[index + 3] = 255;
      }
    }

    ctx.putImageData(imageData, 0, 0);
  }, [hue]);

  const handleCanvasInteraction = e => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const newSaturation = Math.max(0, Math.min(1, x / rect.width));
    const newValue = Math.max(0, Math.min(1, 1 - y / rect.height));

    setSaturation(newSaturation);
    setValue(newValue);

    const rgb = hsvToRgb(hue, newSaturation, newValue);
    const hex = rgbToHex(rgb.r, rgb.g, rgb.b);
    onChange(hex);
  };

  const handleSliderInteraction = e => {
    const slider = sliderRef.current;
    const rect = slider.getBoundingClientRect();
    const x = e.clientX - rect.left;

    const newHue = Math.max(0, Math.min(360, (x / rect.width) * 360));
    setHue(newHue);

    const rgb = hsvToRgb(newHue, saturation, value);
    const hex = rgbToHex(rgb.r, rgb.g, rgb.b);
    onChange(hex);
  };

  return (
    <div className="space-y-4">
      {/* Main color area */}
      <div className="relative">
        <canvas
          ref={canvasRef}
          width={280}
          height={200}
          className="border border-gray-300 rounded cursor-crosshair"
          onMouseDown={e => {
            setIsDragging(true);
            handleCanvasInteraction(e);
          }}
          onMouseMove={e => {
            if (isDragging) handleCanvasInteraction(e);
          }}
          onMouseUp={() => setIsDragging(false)}
          onMouseLeave={() => setIsDragging(false)}
        />
        {/* Saturation/Value indicator */}
        <div
          className="absolute w-4 h-4 border-2 border-white rounded-full pointer-events-none transform -translate-x-2 -translate-y-2 shadow-lg"
          style={{
            left: `${saturation * 280}px`,
            top: `${(1 - value) * 200}px`
          }}
        />
      </div>

      {/* Hue slider */}
      <div className="relative h-6">
        <div
          ref={sliderRef}
          className="w-full h-full rounded cursor-pointer"
          style={{
            background:
              "linear-gradient(to right, #ff0000 0%, #ffff00 17%, #00ff00 33%, #00ffff 50%, #0000ff 67%, #ff00ff 83%, #ff0000 100%)"
          }}
          onMouseDown={e => {
            setIsSliderDragging(true);
            handleSliderInteraction(e);
          }}
          onMouseMove={e => {
            if (isSliderDragging) handleSliderInteraction(e);
          }}
          onMouseUp={() => setIsSliderDragging(false)}
          onMouseLeave={() => setIsSliderDragging(false)}
        />
        {/* Hue indicator */}
        <div
          className="absolute top-0 w-2 h-full bg-white border border-gray-400 rounded-sm pointer-events-none transform -translate-x-1"
          style={{
            left: `${(hue / 360) * 100}%`
          }}
        />
      </div>
    </div>
  );
};

const ColorPicker = ({
  initialColor = "#3b82f6",
  onChange,
  label = "Color Picker"
}) => {
  const [color, setColor] = useState(initialColor);
  const [hexInput, setHexInput] = useState(initialColor);
  const [isInputFocused, setIsInputFocused] = useState(false);

  // Get RGB values from current color
  const rgbValues = hexToRgb(color);

  const handleColorChange = useCallback(
    newColor => {
      setColor(newColor);
      if (!isInputFocused) {
        setHexInput(newColor);
      }

      // Call onChange with both hex and RGB
      if (onChange) {
        const rgb = hexToRgb(newColor);
        onChange({
          hex: newColor,
          rgb: rgb,
          rgbString: `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`
        });
      }
    },
    [isInputFocused, onChange]
  );

  const handleHexInputChange = e => {
    const value = e.target.value;
    setHexInput(value);

    if (isValidHex(value)) {
      setColor(value);
      if (onChange) {
        const rgb = hexToRgb(value);
        onChange({
          hex: value,
          rgb: rgb,
          rgbString: `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`
        });
      }
    }
  };

  const handleInputFocus = () => {
    setIsInputFocused(true);
  };

  const handleInputBlur = () => {
    setIsInputFocused(false);
    // Reset input to current color if invalid
    if (!isValidHex(hexInput)) {
      setHexInput(color);
    }
  };

  // Add global mouse event listeners for dragging
  useEffect(() => {
    const handleMouseUp = () => {
      setIsInputFocused(false);
    };

    document.addEventListener("mouseup", handleMouseUp);
    return () => document.removeEventListener("mouseup", handleMouseUp);
  }, []);

  return (
    <div className="w-full max-w-sm mx-auto p-6 bg-white rounded-lg shadow-lg">
      <label className="block text-sm font-medium text-gray-700 mb-4">
        {label}
      </label>

      {/* Custom Color Picker */}
      <div className="mb-6">
        <CustomColorPicker color={color} onChange={handleColorChange} />
      </div>

      {/* Hex Input */}
      <div className="mb-4">
        <label className="block text-xs font-medium text-gray-600 mb-2">
          Hex Value
        </label>
        <input
          type="text"
          value={hexInput}
          onChange={handleHexInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder="#000000"
          className={`w-full px-3 py-2 border rounded-md text-sm font-mono ${
            isValidHex(hexInput)
              ? "border-gray-300 focus:border-blue-500"
              : "border-red-300 focus:border-red-500"
          } focus:outline-none focus:ring-1 focus:ring-blue-500`}
        />
        {!isValidHex(hexInput) && hexInput && (
          <p className="text-xs text-red-600 mt-1">Invalid hex color</p>
        )}
      </div>

      {/* Color Preview and Values */}
      <div className="space-y-3">
        <div className="flex items-center space-x-3">
          <div
            className="w-12 h-12 rounded-lg border-2 border-gray-200 shadow-inner"
            style={{ backgroundColor: color }}
          />
          <div className="flex-1">
            <div className="text-xs text-gray-600 mb-1">Current Color</div>
            <div className="font-mono text-sm text-gray-900">{color}</div>
          </div>
        </div>

        {/* RGB Values Display */}
        {rgbValues && (
          <div className="bg-gray-50 p-3 rounded-md">
            <div className="text-xs font-medium text-gray-600 mb-2">
              RGB Values
            </div>
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div>
                <span className="text-red-600 font-medium">R:</span>{" "}
                {rgbValues.r}
              </div>
              <div>
                <span className="text-green-600 font-medium">G:</span>{" "}
                {rgbValues.g}
              </div>
              <div>
                <span className="text-blue-600 font-medium">B:</span>{" "}
                {rgbValues.b}
              </div>
            </div>
            <div className="mt-2 text-xs text-gray-600 font-mono">
              rgb({rgbValues.r}, {rgbValues.g}, {rgbValues.b})
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ColorPicker;
